import { ConnectionPositionPair } from '@angular/cdk/overlay';
import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { isMasked } from '@miq/utils';
import { Column, InputType } from '@miq/catalog';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TreeNode } from 'primeng/api';
import { debounceTime, map } from 'rxjs/operators';
import { IOption } from '@shared/models/icommons.model';
import { isNotNull, isNull, pick } from '@shared/functions';

@UntilDestroy()
@Component({
  selector: 'miq-record-naics-input',
  templateUrl: './record-naics-input.component.html',
  styleUrls: ['./record-naics-input.component.scss'],
  standalone: false,
})
export class RecordNaicsInputComponent implements OnInit, OnChanges, OnDestroy {
  @Input() recordDetail: Column;
  @Input() disabled = false;
  @Input() selectionMode: 'single' | 'multiple' | 'checkbox' = 'single';

  @Input() currentValue: any;
  @Output() currentValueChange = new EventEmitter<Column>();

  public naicsCodes: TreeNode[];
  public selectedNodes: TreeNode | TreeNode[] = null;
  public eventTarget: any;
  public searchBar = new UntypedFormControl();
  public isMaskedValue = false;

  #hasChildren = node => node?.children?.length > 0;
  private isOpen = false;
  public overlayPositions: ConnectionPositionPair[] = [
    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top' },
    { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom' },
  ];

  ngOnChanges({ recordDetail, currentValue }: SimpleChanges) {
    if (this.recordDetail != null && this.recordDetail.inputType?.toUpperCase() === InputType.NAICS) {
      /**
       * Update recordDetail with the current value because it has the latest value and
       * it is necessary to update the input
       */
      if (this.recordDetail.value !== this.currentValue) {
        this.recordDetail.value = this.currentValue;
      }

      if (recordDetail || currentValue) {
        this.naicsCodes = this.mapTreeNodes(this.recordDetail.values);
      }
    }
    if (currentValue && this.currentValue !== undefined) {
      this.isMaskedValue = isMasked(this.currentValue);
    }
  }

  ngOnInit() {
    // set up search bar changes
    this.searchBar.valueChanges
      .pipe(
        untilDestroyed(this),
        debounceTime(300),
        map(text => text.trim().toLowerCase())
      )
      .subscribe(this.#filterCodesByText);
  }

  #filterCodesByText = (text: string) => {
    this.naicsCodes = [...this.mapTreeNodes(this.recordDetail.values)];
    if (text?.length >= 2) {
      this.naicsCodes = this.naicsCodes.map(this.expandNode(true)).filter(node => this.#filterNode(text, node));
    }
  };

  #filterNode = (text: string, node: TreeNode) => {
    if (this.#hasChildren(node)) {
      node.children = node.children.filter(childNode => this.#filterNode(text, childNode));
    }
    return this.nodeIncludesText(text, node);
  };

  private anyChildrenContainsText = (text, node) =>
    node.children.some(children => this.nodeIncludesText(text, children));

  private nodeIncludesText = (text: string, node: TreeNode) =>
    node.label.toLowerCase().includes(text) ||
    (this.#hasChildren(node) ? this.anyChildrenContainsText(text, node) : false);

  private expandNode = (isExpanded: boolean) => (node: TreeNode) => {
    node.expanded = isExpanded;
    if (this.#hasChildren(node)) {
      node.children.forEach(this.expandNode(isExpanded));
    }
    return node;
  };

  private mapTreeNodes<T = any>(values: IOption[], parent?: TreeNode): TreeNode<T>[] {
    const _handleSelectedNode = this.isSingleSelectMode
      ? this.#handleSingleSelectedNode
      : this.#handleMultiSelectedNodes;
    // filtering when children null due to one of the values from the lov
    // being "(empty)", as this is not a valid value to be selected and saved
    return values
      .filter(({ value }) => value !== '(empty)')
      .map(filter => {
        const nextTreeNode: TreeNode = this.#toTreeNode(filter, parent);
        nextTreeNode.children = this.#hasChildren(filter) ? this.mapTreeNodes(filter.children, nextTreeNode) : null;
        _handleSelectedNode(nextTreeNode);
        return nextTreeNode;
      });
  }

  #toTreeNode = (filter: IOption, parent: TreeNode): TreeNode =>
    Object.assign({}, filter, { label: filter.value, data: filter.key, parent } as TreeNode);

  get isSingleSelectMode() {
    return this.selectionMode === 'single';
  }

  #handleSingleSelectedNode = (node: TreeNode) => {
    if (this.recordDetail.value === node.key || this.currentValue === node.key) {
      this.selectedNodes = node;
      return;
    }
    node.children?.forEach(this.#handleSingleSelectedNode);
  };

  #handleMultiSelectedNodes = (node: TreeNode): void => {
    if (Array.isArray(this.recordDetail.value)) {
      // ensure array exist
      if (this.selectedNodes == null) this.selectedNodes = [];
      // recursively find and push selected nodes
      this.#findAndPushMultiSelectedNodes(node);
    }
  };

  #findAndPushMultiSelectedNodes = (node: TreeNode): void => {
    // push selected found
    if (this.recordDetail.value.includes(node.data) || this.currentValue.includes(node.data)) {
      this.#selectedNodesArr.push(node);
      this.#flagPartialSelections(node, this.recordDetail.value);
    }
    node.children?.forEach(this.#findAndPushMultiSelectedNodes);
  };

  /**
   * Given a treeNode object, ensure every ancestor is flagged as partialSelected if is not part of selected values
   * @param node
   */
  #flagPartialSelections(node: TreeNode, selectedValues: string[]): void {
    let nextParent = node.parent;
    while (nextParent != null) {
      // cannot use directly condition assignment, it causes a weird effect in selection reflection (primeng issue)
      if (nextParent && !nextParent.partialSelected && !selectedValues.includes(nextParent.data)) {
        nextParent.partialSelected = true;
      }
      nextParent = nextParent.parent;
    }
  }

  get #selectedNodesArr() {
    return this.selectedNodes as TreeNode[];
  }

  public handleNodeSelection(node: TreeNode) {
    if (this.isSingleSelectMode) {
      this.recordDetail.value = this.recordDetail.valueSetter ? this.recordDetail.valueSetter(node.data) : node.data;
      this.currentValueChange.emit(this.recordDetail);
    }
  }

  public clearSelectedNode(): void {
    if (this.isSingleSelectMode) {
      this.handleNodeSelection({ data: null });
      this.selectedNodes = null;
      return;
    }
    if (this.selectedNodes != null) {
      this.selectedNodes = [];
      this.recordDetail.value = null;
      this.currentValueChange.emit(this.recordDetail);
    }
  }

  public toggleOverlay() {
    if (this.disabled) {
      return;
    }
    this.isOpen = !this.isOpen;
  }

  public closeOverlay(): void {
    this.isOpen = false;
    if (!this.isSingleSelectMode) {
      // multiple || checkbox
      this.#emitSelectedNodes();
    }
  }

  #emitSelectedNodes() {
    const nextValue = this.#selectedNodesArr?.map(pick('data'));
    if (this.#isSelectionDifferent(nextValue, this.recordDetail.value)) {
      this.recordDetail.value = nextValue;
      this.currentValueChange.emit(this.recordDetail);
    }
  }

  #isSelectionDifferent = (nextValues: string[], currentValues: string[]): boolean => {
    const comparedValues = [nextValues, currentValues];
    if (nextValues != currentValues && comparedValues.some(x => isNull(x)) && comparedValues.some(x => isNotNull(x))) {
      return true;
    }
    const copiedSortedJoinedArray = arr => [...arr].sort((a, b) => a.localeCompare(b)).join();
    return copiedSortedJoinedArray(nextValues) !== copiedSortedJoinedArray(currentValues);
  };

  ngOnDestroy() {
    // Required for untilDestroyed
  }
}
